import type { ReactNode } from 'react';
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import gql from 'graphql-tag';
import { type ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop';
import { useSubscription } from '@apollo/react-hooks';
import uuid from 'uuid/v4';

import { fg } from '@confluence/feature-gating';
import { markErrorAsHandled } from '@confluence/graphql-error-processor/entry-points/GraphqlErrorProcessor';
import { setContentErrorAttributes } from '@confluence/monitoring/entry-points/error-attributes';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery';

import { getAGGClient } from './getAGGClient';
import { useSubscriptionValues } from './links/AGGWebSocketLink/useSubscriptionValues';
import type {
	ConfluenceOnContentModified,
	SubscriptionsDispatchType,
	onSubscriptionDataType,
} from './subscriptionsTypes';

type SubscriptionsContextType = {
	contentModified: ConfluenceOnContentModified | null;
	error?: ApolloErrorInterop;
};

const SubscriptionsContext = createContext<SubscriptionsContextType | undefined>(undefined);
const SubscriptionsDispatchContext = createContext<SubscriptionsDispatchType | undefined>(
	undefined,
);

export const errorStringsToIgnore = [
	'401',
	'Failed to fetch',
	'Load Failed',
	'Network error',
	'GraphQL error: undefined',
	'Auth category: UNAUTHENTICATED is not allowed in service confluence_monolith_subscriptions',
];

type Subscriber = {
	id: string;
	onSubscriptionData: onSubscriptionDataType;
};
type SubscribersMap = Record<string, Subscriber[]>;

export const SubscriptionsContextProvider = ({ children }: { children: ReactNode }) => {
	const [contentModified, setContentModified] = useState<ConfluenceOnContentModified | null>(null);
	const [contentId, setContentId] = useState<string | null>(null);
	const [contentType, setContentType] = useState<string | null>(null);
	const subscribersRef = React.useRef<SubscribersMap>({});
	// if there are no subscribers left we set the contentId and contentType to null
	// remove subscriber on unmount of subscription component

	/***
	 * Adds a subscriber to the context.
	 * @delta - the delta to subscribe to
	 * @subscriberId - an id for the subscriber, used to remove the subscriber later
	 * @onSubscriptionData - callback to be called when data is received for the delta
	 * @returns uniqueSubscriberId - a unique id for the subscriber, used to remove the subscriber later
	 */
	const addSubscriber = useCallback(
		({
			subscriberId,
			onSubscriptionData,
			delta,
		}: {
			subscriberId: string;
			onSubscriptionData: onSubscriptionDataType;
			delta: string;
		}) => {
			const subscribers = subscribersRef.current;
			const uniqueSubscriberId = `${subscriberId}-${uuid()}`; // ensure unique subscriberId
			const currentSubscribersForDelta = subscribers?.[delta] || [];
			subscribers[delta] = [
				...currentSubscribersForDelta,
				{ id: uniqueSubscriberId, onSubscriptionData },
			];

			return uniqueSubscriberId;
		},
		[],
	);

	const removeSubscriber = useCallback(
		({ delta, subscriberId }: { delta: string; subscriberId: string }) => {
			const subscribers = subscribersRef.current;
			if (subscribers.hasOwnProperty(delta)) {
				subscribers[delta] = (subscribers[delta] as Subscriber[]).filter(
					(sub) => sub.id !== subscriberId,
				);
				// if there are no subscribers left for this delta, we can remove the delta
				if (subscribers[delta].length === 0) {
					delete subscribers[delta];
					// if that was the last subscriber we can stop the subscription entirely
					if (Object.keys(subscribers).length === 0) {
						setContentId(null);
						setContentType(null);
					}
				}
			}
		},
		[setContentId, setContentType],
	);

	const callSubscribers = useCallback((deltas: string[], data: ConfluenceOnContentModified) => {
		const subscribers = subscribersRef.current;
		deltas.forEach((delta: string) => {
			if (subscribers.hasOwnProperty(delta)) {
				(subscribers?.[delta] as Subscriber[]).forEach((subscriber) => {
					const callback = subscriber?.onSubscriptionData?.(delta, data);
					if (callback instanceof Promise) {
						Promise.resolve(callback).catch((err) => {
							getMonitoringClient().submitError(err, {
								// hard coded as the package will cause circular dependency
								attribution: 'page-experiences',
							});
						});
					}
				});
			}
		});
	}, []);

	const {
		id,
		onSubscriptionComplete,
		extensions,
		onSubscriptionData: onDataReceived,
	} = useSubscriptionValues({
		contentType,
		contentId,
	});

	const { accessStatus } = useSessionData();
	const skipSubscriptionForAnonUsers = accessStatus === AccessStatus.ANONYMOUS_ACCESS;

	// eslint-disable-next-line graphql-client-usage/graphql-generic-types
	const { error } = useSubscription(
		gql`
			subscription SubscriptionsContextProviderSubscription($id: ID!) {
				confluence_onContentModified(id: $id) {
					type
					id
					accountId
					__typename
					_deltas
					contentStateUpdated {
						key
						value
						version
						__typename
					}
					emojiTitleUpdated {
						key
						value
						version
						__typename
					}
					coverPictureUpdated {
						key
						value
						version
						__typename
					}
					pageTitlePropertyUpdated {
						key
						value
						version
						__typename
					}
					coverPictureDeleted {
						key
						__typename
					}
					emojiTitleDeleted {
						key
						__typename
					}
					contentStateDeleted {
						key
						__typename
					}
					coverPictureWidthUpdated {
						coverPictureWidth
						__typename
					}
					pageBlogified {
						spaceKey
						spaceName
						blogTitle
						converterDisplayName
						__typename
					}
					embedUpdated {
						product
						isBlankStateUpdate
						__typename
					}
					pageMigrated {
						fabricEligibility
						__typename
					}
					contentRestrictionUpdated {
						contentId
						__typename
					}
					pageMoved {
						oldSpaceKey
						newSpaceKey
						oldParentId
						oldPosition
						newSpaceAlias
						oldSpaceAlias
						__typename
					}
					pageUpdated {
						__typename
						trigger
						confVersion
					}

					contentUpdatedWithTemplate {
						spaceKey
						subtype
						__typename
					}

					inlineCommentCreated {
						commentId
						inlineCommentType
						adfBodyContent
						__typename
						markerRef
					}
					rendererInlineCommentCreated {
						__typename
						commentId
						adfBodyContent
						inlineCommentType
						markerRef
						publishVersionNumber
						step {
							from
							to
							pos
							mark {
								attrs {
									annotationType
									__typename
								}
								__typename
							}
							__typename
						}
					}

					editorInlineCommentCreated {
						adfBodyContent
						commentId
						inlineCommentType
						markerRef
						__typename
					}

					inlineCommentUpdated {
						commentId
						markerRef
						__typename
					}

					inlineCommentDeleted {
						commentId
						inlineCommentType
						markerRef
						__typename
					}
					inlineCommentResolved {
						commentId
						inlineResolveProperties {
							isDangling
							resolved
							resolvedByDangling
							resolvedFriendlyDate
							resolvedTime
							resolvedUser
							__typename
						}
						inlineText
						markerRef
						replies
						__typename
					}

					inlineCommentUnresolved {
						commentId
						markerRef
						__typename
					}

					inlineCommentReattached {
						commentId
						__typename
						markerRef
						publishVersionNumber
						step {
							from
							mark {
								attrs {
									__typename
									annotationType
								}
								__typename
							}
							__typename
						}
					}

					commentCreated {
						__typename
						adfBodyContent
						commentId
						pageCommentType
					}

					commentUpdated {
						__typename
						commentId
					}

					commentDeleted {
						__typename
						commentId
					}

					commentResolved {
						__typename
						adfBodyContent
						commentId
						pageCommentType
						replies
					}

					commentReopened {
						__typename
						commentId
					}

					schedulePublished {
						publishTime
						eventType
						confVersion
						__typename
					}

					contentAccessRequested {
						contentId
						__typename
					}
				}
			}
		`,
		{
			variables: {
				id,
				__extensions: extensions,
			},
			onSubscriptionComplete,
			onSubscriptionData(response) {
				onDataReceived(response);
				const { subscriptionData } = response;
				if (!subscriptionData || !subscriptionData.data) {
					return;
				}
				const deltas = subscriptionData?.data?.confluence_onContentModified?._deltas;
				const data = subscriptionData?.data?.confluence_onContentModified;
				if (deltas && Array.isArray(deltas) && deltas.length > 0 && data) {
					callSubscribers(deltas, data);
				}
				setContentModified(subscriptionData?.data?.confluence_onContentModified ?? null);
			},
			skip:
				!id ||
				skipSubscriptionForAnonUsers ||
				!fg('confluence_enable_subscriptions-context-provider') ||
				!fg('confluence_enable_agg_subscription_link'),
			client: getAGGClient(),
		},
	);

	useEffect(() => {
		if (error) {
			const errorMessage =
				error?.message ||
				(error as any)?.errors?.[0]?.message ||
				error?.graphQLErrors?.[0]?.message ||
				error?.graphQLErrors?.[0]?.originalError?.message ||
				error?.graphQLErrors?.[0]?.extensions?.data?.errors?.[0]?.message ||
				error?.graphQLErrors?.[0]?.extensions?.message;
			const statusCode = (error as any)?.errors?.[0]?.extensions?.statusCode;

			// if there is no error message it is likely to be a client side network issue
			// eg spotty vpn
			const isIgnoredError =
				errorStringsToIgnore.some((errorString) => error?.message.includes(errorString)) ||
				!errorMessage;

			if (statusCode !== 403 && !isIgnoredError) {
				const e = new Error(`Subscription Error - :${errorMessage}`);
				setContentErrorAttributes(e, {
					objectId: contentId,
					objectType: contentType,
				});
				getMonitoringClient().submitError(e, {
					// hard coded as the package will cause circular dependency
					attribution: 'page-experiences',
				});
			}
			markErrorAsHandled(error);
		}
	}, [error, contentId, contentType]);

	const contextValue = React.useMemo(() => ({ contentModified, error }), [contentModified, error]);
	const dispatchContextValue = React.useMemo(
		() => ({
			setContentType,
			setContentId,
			addSubscriber,
			removeSubscriber,
		}),
		[setContentType, setContentId, addSubscriber, removeSubscriber],
	);

	if (fg('confluence_enable_subscriptions-context-provider')) {
		return (
			<SubscriptionsContext.Provider value={contextValue}>
				<SubscriptionsDispatchContext.Provider value={dispatchContextValue}>
					{children}
				</SubscriptionsDispatchContext.Provider>
			</SubscriptionsContext.Provider>
		);
	} else {
		return <>{children}</>;
	}
};

export const useSubscriptionsContext = () => {
	return useContext(SubscriptionsContext);
};

export const useSubscriptionsDispatchContext = () => {
	return useContext(SubscriptionsDispatchContext);
};
